// frontend/src/pages/AdminFilesPage.tsx
import { useCallback, useEffect, useState } from 'react';
import { Navigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { PageLoader, LoadingSpinner } from '../components/LoadingSpinner';
import { dbSelect, dbUpdate } from '../lib/api';
import {
  Search,
  FileCode,
  Download,
  ChevronDown,
  ChevronRight,
  Copy,
  Check,
  FolderOpen,
  AlertCircle,
  ExternalLink,
  User,
  ArrowLeft,
} from 'lucide-react';

const ADMIN_EMAIL = 'a.burkov@gmail.com';

export function AdminFilesPage() {
  const { user, session, loading: authLoading } = useAuth();
  const [searchParams] = useSearchParams();

  const [urlInput, setUrlInput] = useState('');
  const [projectId, setProjectId] = useState<string | null>(null);
  const [projectInfo, setProjectInfo] = useState<{
    name: string;
    description: string | null;
    user_email: string;
    user_display_name: string | null;
    created_at: string;
  } | null>(null);
  const [projectTitleInput, setProjectTitleInput] = useState('');
  const [projectDescriptionInput, setProjectDescriptionInput] = useState('');
  const [metadataSaving, setMetadataSaving] = useState(false);
  const [metadataMessage, setMetadataMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [files, setFiles] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedFile, setExpandedFile] = useState<string | null>(null);
  const [copiedFile, setCopiedFile] = useState<string | null>(null);
  const [autoLoadedFromQuery, setAutoLoadedFromQuery] = useState(false);

  useEffect(() => {
    document.title = 'Admin Files - Llambada';
    return () => { document.title = 'Llambada'; };
  }, []);

  type ParsedProjectRef =
    | { kind: 'projectId'; value: string }
    | { kind: 'publicId'; value: string };

  const parseProjectRef = (url: string): ParsedProjectRef | null => {
    const input = url.trim();
    if (!input) return null;

    const uuidPattern = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}';
    const projectUrlMatch = input.match(new RegExp('/project/(' + uuidPattern + ')(?:$|[/?#])', 'i'));
    if (projectUrlMatch) return { kind: 'projectId', value: projectUrlMatch[1] };

    const rawUuidMatch = input.match(new RegExp('^(' + uuidPattern + ')$', 'i'));
    if (rawUuidMatch) return { kind: 'projectId', value: rawUuidMatch[1] };

    // Public URL format: /p/<public_id>/<optional-slug>
    const publicUrlMatch = input.match(/\/p\/([^/?#]+)/i);
    if (publicUrlMatch) return { kind: 'publicId', value: publicUrlMatch[1] };

    // Allow directly entering just public_id
    const rawPublicIdMatch = input.match(/^[A-Za-z0-9]{8}$/);
    if (rawPublicIdMatch) return { kind: 'publicId', value: rawPublicIdMatch[0] };

    return null;
  };

  const fetchProjectFiles = useCallback(async (inputOverride?: string) => {
    if (!session) return;

    const parsedRef = parseProjectRef(inputOverride ?? urlInput);
    if (!parsedRef) {
      setError('Could not parse a project reference. Expected /project/<uuid>, /p/<publicId>/<slug>, UUID, or publicId.');
      return;
    }

    setLoading(true);
    setError(null);
    setFiles({});
    setProjectInfo(null);
    setProjectTitleInput('');
    setProjectDescriptionInput('');
    setMetadataMessage(null);
    setExpandedFile(null);

    try {
      let id = parsedRef.value;
      if (parsedRef.kind === 'publicId') {
        const { data: projectByPublicId, error: projectLookupError } = await dbSelect<{ id: string }>(
          'projects',
          {
            token: session.access_token,
            select: 'id',
            filters: { public_id: 'eq.' + parsedRef.value },
            maybeSingle: true,
          }
        );

        if (projectLookupError) {
          throw new Error('Failed to resolve public project URL: ' + projectLookupError.message);
        }

        if (!projectByPublicId?.id) {
          throw new Error('No project found for public ID: ' + parsedRef.value);
        }

        id = projectByPublicId.id;
      }

      setProjectId(id);

      // Fetch project metadata
      const { data: project, error: projectError } = await dbSelect<{
        id: string;
        name: string;
        description: string | null;
        user_id: string;
        created_at: string;
        profiles: { email: string; display_name: string | null } | null;
      }>('projects', {
        token: session.access_token,
        select: 'id,name,description,user_id,created_at,profiles!projects_user_id_fkey(email,display_name)',
        filters: { id: 'eq.' + id },
        single: true,
      });

      if (projectError) {
        setError('Failed to fetch project metadata: ' + projectError.message);
      } else if (project) {
        const prof = Array.isArray((project as any).profiles)
          ? (project as any).profiles[0]
          : (project as any).profiles;
        setProjectInfo({
          name: project.name,
          description: project.description,
          user_email: prof?.email || 'Unknown',
          user_display_name: prof?.display_name || null,
          created_at: project.created_at,
        });
        setProjectTitleInput(project.name);
        setProjectDescriptionInput(project.description || '');
      }

      // Fetch project files via the project-code Edge Function
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const response = await fetch(
        supabaseUrl + '/functions/v1/project-code?projectId=' + id,
        {
          method: 'GET',
          headers: {
            'Authorization': 'Bearer ' + session.access_token,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'HTTP ' + response.status);
      }

      const result = await response.json();
      const fetchedFiles = result.files || result || {};

      const cleanFiles: Record<string, string> = {};
      for (const [key, value] of Object.entries(fetchedFiles)) {
        if (typeof value === 'string') {
          cleanFiles[key] = value;
        }
      }

      setFiles(cleanFiles);
      if (Object.keys(cleanFiles).length === 0) {
        setError('No files found for this project.');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch project files');
    } finally {
      setLoading(false);
    }
  }, [session, urlInput]);

  useEffect(() => {
    if (!session || autoLoadedFromQuery) return;

    const projectIdFromQuery = searchParams.get('projectId') || searchParams.get('project');
    const publicIdFromQuery = searchParams.get('publicId');
    const rawTarget = projectIdFromQuery || publicIdFromQuery;
    if (!rawTarget) return;

    const normalizedInput = projectIdFromQuery ? `/project/${rawTarget}` : rawTarget;
    setUrlInput(normalizedInput);
    setAutoLoadedFromQuery(true);
    fetchProjectFiles(normalizedInput);
  }, [session, autoLoadedFromQuery, searchParams, fetchProjectFiles]);

  const downloadFile = (filename: string, content: string) => {
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename.split('/').pop() || filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const downloadAllFiles = () => {
    Object.entries(files).forEach(([filename, content]) => {
      downloadFile(filename, content);
    });
  };

  const copyToClipboard = async (filename: string, content: string) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedFile(filename);
      setTimeout(() => setCopiedFile(null), 2000);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = content;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopiedFile(filename);
      setTimeout(() => setCopiedFile(null), 2000);
    }
  };

  const getLanguageFromFilename = (filename: string): string => {
    const ext = filename.split('.').pop()?.toLowerCase();
    const langMap: Record<string, string> = {
      py: 'Python', js: 'JavaScript', ts: 'TypeScript', tsx: 'TSX', jsx: 'JSX',
      html: 'HTML', css: 'CSS', json: 'JSON', yml: 'YAML', yaml: 'YAML',
      md: 'Markdown', txt: 'Text', sh: 'Shell',
    };
    if (filename.toLowerCase() === 'dockerfile' || filename.toLowerCase().endsWith('/dockerfile')) return 'Dockerfile';
    return langMap[ext || ''] || ext?.toUpperCase() || 'Text';
  };

  const formatFileSize = (content: string): string => {
    const bytes = new TextEncoder().encode(content).length;
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const sortedFiles = Object.entries(files).sort(([a], [b]) => a.localeCompare(b));
  const titleTrimmed = projectTitleInput.trim();
  const descriptionTrimmed = projectDescriptionInput.trim();
  const metadataDirty = !!projectInfo && (
    titleTrimmed !== projectInfo.name ||
    descriptionTrimmed !== (projectInfo.description || '')
  );

  const saveProjectMetadata = async () => {
    if (!session || !projectId || !projectInfo) return;
    if (!titleTrimmed) {
      setMetadataMessage({ type: 'error', text: 'Title is required.' });
      return;
    }

    setMetadataSaving(true);
    setMetadataMessage(null);

    try {
      const { error: updateError } = await dbUpdate(
        'projects',
        {
          name: titleTrimmed,
          description: descriptionTrimmed || null,
        },
        { id: `eq.${projectId}` },
        { token: session.access_token, returning: false }
      );

      if (updateError) throw updateError;

      setProjectInfo((prev) => prev ? {
        ...prev,
        name: titleTrimmed,
        description: descriptionTrimmed || null,
      } : prev);
      setProjectTitleInput(titleTrimmed);
      setProjectDescriptionInput(descriptionTrimmed);
      setMetadataMessage({ type: 'success', text: 'Mini-app metadata updated.' });
    } catch (err) {
      setMetadataMessage({
        type: 'error',
        text: err instanceof Error ? err.message : 'Failed to update mini-app metadata.',
      });
    } finally {
      setMetadataSaving(false);
    }
  };

  if (authLoading) return <PageLoader />;

  if (!user || user.email !== ADMIN_EMAIL) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <Layout>
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <Link
            to="/admin"
            className="flex h-10 w-10 items-center justify-center rounded-lg bg-theme-surface hover:bg-theme-bg-muted transition-colors"
            title="Back to Admin"
          >
            <ArrowLeft className="h-5 w-5 text-theme-text-muted" />
          </Link>
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-500/10 text-blue-500">
            <Search className="h-6 w-6" />
          </div>
          <div className="flex-1">
            <h1 className="font-display text-2xl font-bold text-theme-text">
              Project File Inspector
            </h1>
            <p className="text-theme-text-secondary text-sm">
              View and download current file versions for any project
            </p>
          </div>
        </div>

        {/* Search Input */}
        <div className="card p-6 mb-6">
          <div className="flex gap-3">
            <input
              type="text"
              value={urlInput}
              onChange={(e) => setUrlInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); fetchProjectFiles(); } }}
              placeholder="http://localhost:3012/project/<uuid> or http://localhost:3012/p/<publicId>/<slug>"
              className="input flex-1 font-mono text-sm"
              disabled={loading}
            />
            <button
              onClick={() => { void fetchProjectFiles(); }}
              disabled={loading || !urlInput.trim()}
              className="btn btn-primary shrink-0"
            >
              {loading ? (
                <>
                  <LoadingSpinner size="sm" />
                  Loading...
                </>
              ) : (
                <>
                  <Search className="h-4 w-4" />
                  Inspect
                </>
              )}
            </button>
          </div>

          {error && (
            <div className="flex items-center gap-2 text-sm p-3 rounded-lg bg-theme-error-bg text-theme-error mt-4">
              <AlertCircle className="h-4 w-4 shrink-0" />
              {error}
            </div>
          )}
        </div>

        {/* Project Info Card */}
        {projectInfo && (
          <div className="card p-6 mb-6">
            <div className="flex items-center justify-between gap-4">
              <div>
                <h2 className="text-lg font-semibold text-theme-text">{projectInfo.name}</h2>
                <div className="flex items-center gap-3 mt-1 text-sm text-theme-text-muted">
                  <span className="flex items-center gap-1">
                    <User className="h-3.5 w-3.5" />
                    {projectInfo.user_display_name || projectInfo.user_email}
                  </span>
                  <span>·</span>
                  <span>
                    Created {new Date(projectInfo.created_at).toLocaleDateString()}
                  </span>
                </div>
              </div>
              {projectId && (
                <Link
                  to={'/project/' + projectId}
                  target="_blank"
                  className="btn btn-secondary btn-sm"
                >
                  <ExternalLink className="h-4 w-4" />
                  Open Project
                </Link>
              )}
            </div>

            <div className="mt-5 border-t border-theme-border-muted pt-5 space-y-4">
              <div>
                <label htmlFor="adminProjectTitle" className="block text-sm font-medium text-theme-text-secondary mb-1.5">
                  Mini-App Title
                </label>
                <input
                  id="adminProjectTitle"
                  type="text"
                  value={projectTitleInput}
                  onChange={(e) => {
                    setProjectTitleInput(e.target.value);
                    if (metadataMessage) setMetadataMessage(null);
                  }}
                  className="input"
                  disabled={metadataSaving}
                  maxLength={120}
                />
              </div>

              <div>
                <label htmlFor="adminProjectDescription" className="block text-sm font-medium text-theme-text-secondary mb-1.5">
                  Mini-App Description
                </label>
                <textarea
                  id="adminProjectDescription"
                  value={projectDescriptionInput}
                  onChange={(e) => {
                    setProjectDescriptionInput(e.target.value);
                    if (metadataMessage) setMetadataMessage(null);
                  }}
                  className="input min-h-[96px] resize-y"
                  disabled={metadataSaving}
                  maxLength={1000}
                />
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <button
                  onClick={saveProjectMetadata}
                  disabled={metadataSaving || !metadataDirty || !titleTrimmed}
                  className="btn btn-primary btn-sm"
                >
                  {metadataSaving ? (
                    <>
                      <LoadingSpinner size="sm" />
                      Saving...
                    </>
                  ) : (
                    'Save Metadata'
                  )}
                </button>

                <button
                  onClick={() => {
                    setProjectTitleInput(projectInfo.name);
                    setProjectDescriptionInput(projectInfo.description || '');
                    setMetadataMessage(null);
                  }}
                  disabled={metadataSaving || !metadataDirty}
                  className="btn btn-secondary btn-sm"
                >
                  Reset
                </button>

                {metadataMessage && (
                  <div
                    className={`text-sm ${
                      metadataMessage.type === 'success' ? 'text-theme-success' : 'text-theme-error'
                    }`}
                  >
                    {metadataMessage.text}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* File List */}
        {sortedFiles.length > 0 && (
          <div className="card p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2 text-sm text-theme-text-muted">
                <FolderOpen className="h-4 w-4" />
                <span>{sortedFiles.length} file{sortedFiles.length !== 1 ? 's' : ''}</span>
                <span className="text-theme-text-muted/50">·</span>
                <span>{formatFileSize(Object.values(files).join(''))}</span>
              </div>
              <button onClick={downloadAllFiles} className="btn btn-secondary btn-sm">
                <Download className="h-4 w-4" />
                Download All
              </button>
            </div>

            <div className="space-y-2">
              {sortedFiles.map(([filename, content]) => {
                const isExpanded = expandedFile === filename;
                return (
                  <div key={filename} className="border border-theme-border-muted rounded-lg overflow-hidden">
                    {/* File header */}
                    <div
                      className="flex items-center gap-3 px-4 py-3 bg-theme-surface/50 cursor-pointer hover:bg-theme-surface/80 transition-colors"
                      onClick={() => setExpandedFile(isExpanded ? null : filename)}
                    >
                      {isExpanded ? (
                        <ChevronDown className="h-4 w-4 text-theme-text-muted shrink-0" />
                      ) : (
                        <ChevronRight className="h-4 w-4 text-theme-text-muted shrink-0" />
                      )}
                      <FileCode className="h-4 w-4 text-accent-500 shrink-0" />
                      <span className="font-mono text-sm text-theme-text flex-1 truncate">{filename}</span>
                      <span className="text-xs text-theme-text-muted shrink-0">{getLanguageFromFilename(filename)}</span>
                      <span className="text-xs text-theme-text-muted shrink-0 ml-2">{formatFileSize(content)}</span>
                      <div className="flex items-center gap-1 ml-2 shrink-0" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={() => copyToClipboard(filename, content)}
                          className="p-1.5 rounded hover:bg-theme-surface transition-colors text-theme-text-muted hover:text-theme-text"
                          title="Copy to clipboard"
                        >
                          {copiedFile === filename ? (
                            <Check className="h-3.5 w-3.5 text-theme-success" />
                          ) : (
                            <Copy className="h-3.5 w-3.5" />
                          )}
                        </button>
                        <button
                          onClick={() => downloadFile(filename, content)}
                          className="p-1.5 rounded hover:bg-theme-surface transition-colors text-theme-text-muted hover:text-theme-text"
                          title="Download file"
                        >
                          <Download className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>

                    {/* File content */}
                    {isExpanded && (
                      <div className="border-t border-theme-border-muted">
                        <pre className="p-4 text-sm font-mono text-theme-text-secondary overflow-x-auto max-h-[32rem] bg-theme-bg-muted/50">
                          <code>{content}</code>
                        </pre>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
